import React, { useState, useEffect } from "react";
import { MSG } from "../constants/toastMessages";
import '../assets/css/edit.css';
import { useDispatch, useSelector } from "react-redux";
import { setIsAuthenticated } from "../redux/reducers/authSlice";
import { setUserData } from "../redux/reducers/masterDataSlice";
import RegexPattern from "../utils/regexPattern";
import { useNavigate, useLocation } from 'react-router-dom';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { apiCallingPost } from "../api/services";
import { toast } from 'react-toastify';
import SignupForm from "./signup-form";
import Loader from "../assets/images/loader.gif"
import ForgotPassword from "./forgotPassword"
function Login() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [userName, setUserName] = useState("");
  const [password, setPassword] = useState("");
  const [signup, showSignup] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [submitFlag, setSubmitFlag] = useState(false);
  const { pageUrl } = useSelector((state) => state.authentication);
  const [loading, setLoading] = useState(false)
  const [step, setStep] = useState("");
  const [tempToken, setTempToken] = useState("");
  const [qr, setQr] = useState("");
  const [code, setCode] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const location = useLocation();
  useEffect(() => {
    // If we arrived from email verification, prefill the email and focus the password;
    // otherwise focus the email field as usual.
    const prefillEmail = location.state?.email;
    if (prefillEmail) {
      setUserName(prefillEmail);
      document.getElementById("password")?.focus();
    } else {
      document.getElementById("username")?.focus();
    }

    // 🔹 Handle Google redirect (id_token comes in URL hash)
    if (window.location.hash) {
      const hashParams = new URLSearchParams(window.location.hash.substring(1));
      const idToken = hashParams.get("id_token");
      if (idToken) {
        handleCredentialResponse({ credential: idToken });
        // Optional: clean the URL so hash doesn’t stay visible
        window.history.replaceState(null, "", window.location.pathname);
      }
    }
  }, []);

  const handleCredentialResponse = async (response) => {
    setLoading(true)
    try {
      const data = await apiCallingPost("api/authentication/googleAuth", {
        token: response.credential,
      });

      if (data.data.success === "1") {
        const userRow = data.data.userData?.[0];
        if (!userRow || typeof userRow !== "object") {
          dispatch(setIsAuthenticated(false));
          dispatch(setUserData([]));
          setLoading(false);
          toast.error(data.data.errorMessage || "Login failed. Please contact your administrator.");
          return;
        }
        if (data.data.sessionToken) {
          window.localStorage.setItem("sessionToken", data.data.sessionToken);
        }
        dispatch(setIsAuthenticated(true));
        dispatch(setUserData(userRow));
        toast.success(data.data.successMessage);
        const dest = userRow.roleDisplayName === "Super_admin" ? "/media-requests" : (pageUrl || "/");
        setTimeout(() => {
          setLoading(false);
          navigate(dest);
        }, 1500);
      } else {
        dispatch(setIsAuthenticated(false));
        dispatch(setUserData([]));
        toast.error(data.data.errorMessage || "Login Failed!");
        setLoading(false);
      }
    } catch (err) {
      toast.error(MSG.loginFailed);
      console.error("Login failed", err);
      setLoading(false);
    }
  };

  // EMAIL/PASSWORD LOGIN
  const handleLogin = async (e) => {
    setLoading(true)
    e.preventDefault();
    setSubmitFlag(true);

    if (!userName) {
      toast.error(MSG.emailRequired);
      setSubmitFlag(false);
      setLoading(false)
      return;
    }

    if (!userName.match(RegexPattern.Email)) {
      toast.error(MSG.emailInvalid);
      setSubmitFlag(false);
      setLoading(false)
      return;
    }

    if (!password) {
      toast.error(MSG.passwordRequired);
      setSubmitFlag(false);
      setLoading(false)
      return;
    }

    try {
      let postData = {
        email: userName,
        password: password,
      };
      const data = await apiCallingPost("api/authentication/googleLogin", postData);

      if (data.data.success === "1") {
        const userRow = data.data.userData?.[0];
        if (!userRow || typeof userRow !== "object") {
          dispatch(setIsAuthenticated(false));
          dispatch(setUserData([]));
          setLoading(false);
          toast.error(data.data.errorMessage || "Login failed. Please contact your administrator.");
          return;
        }
        if (userRow.mfaEnabled) {
          setTempToken(data.data.tempToken);
          setCode("");
          setLoading(false);
          setStep("mfa");
        } else {
          // No MFA yet: the backend returns an enrollment token bound to this
          // password login. Thread it directly into enrollMfa (state set is async).
          setTempToken(data.data.tempToken);
          enrollMfa(data.data.tempToken);
        }
      } else if (data.data.needsVerification) {
        // Account exists but email isn't verified — block login, offer a resend.
        dispatch(setIsAuthenticated(false));
        dispatch(setUserData([]));
        setLoading(false);
        toast.error(data.data.errorMessage);
        toast.info("Didn't get the email? Click here to resend the verification link.", {
          autoClose: false,
          onClick: () => resendVerification(userName),
        });
      } else {
        dispatch(setIsAuthenticated(false));
        dispatch(setUserData([]));
        toast.error(data.data.errorMessage);
        setLoading(false);
      }
    } catch (err) {
      toast.error(MSG.loginFailed);
      setLoading(false);
    } finally {
      setSubmitFlag(false);
    }
  };

  // 🔹 Employee Login → Redirect-based Google OAuth
  const handleClick = () => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    const redirectUri = process.env.REACT_APP_GOOGLE_LOGIN_REDIRECT_URL; // must match in Google Console

    const oauth2Endpoint = "https://accounts.google.com/o/oauth2/v2/auth";
    const nonce = Math.random().toString(36).substring(2);
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: "token id_token", // ✅ tokens only
      scope: "openid email profile",
      include_granted_scopes: "true",
      state: "employee-login",
      nonce: nonce
    });
    // Redirect to Google OAuth
    window.location.href = `${oauth2Endpoint}?${params.toString()}`;
  };
  const resendVerification = async (email) => {
    try {
      const data = await apiCallingPost("api/authentication/resendVerification", { emaild: email });
      toast.success(data?.data?.successMessage || "Verification email sent.");
    } catch (e) {
      toast.error(MSG.genericError);
      console.error("🚀 ~ resendVerification ~ error:", e);
    }
  };
  const enrollMfa = async (enrollToken) => {
    try {
      const postData = {
        tempToken: enrollToken || tempToken,
      };
      const data = await apiCallingPost("api/mfa/enroll-totp", postData);
      if (data.data.success !== "1") {
        toast.error(data.data.errorMessage || "Could not start MFA setup. Please sign in again.");
        setStep("");
        setLoading(false);
        return;
      }
      setQr(data.data.result.qrImageUrl);
      setCode("");
      setLoading(false);
      setStep("enroll");

    } catch (error) {
      toast.error(MSG.loginFailed);
      console.error("🚀 ~ Login ~ error:", error);
      setLoading(false);
    }
  }
  const verifyTotp = async (e) => {
    if (verifying) return;                        // guard against double-submit
    setVerifying(true);
    try {
      const postData = {
        tempToken: tempToken,
        token: (code || "").trim()                // strip whitespace (pasted codes)
      }
      const data = await apiCallingPost("api/mfa/verify-totp-login", postData);
      if (data.data.success === "1") {
        const userRow = data.data.result?.userData?.[0];
        if (!userRow || typeof userRow !== "object") {
          dispatch(setIsAuthenticated(false));
          dispatch(setUserData([]));
          setStep("");
          setLoading(false);
          toast.error(data.data.errorMessage || "Login failed. Please contact your administrator.");
          return;
        }
        if (data.data.sessionToken) {
          window.localStorage.setItem("sessionToken", data.data.sessionToken);
        }
        dispatch(setIsAuthenticated(true));
        dispatch(setUserData(userRow));
        setStep("");
        const dest = userRow.roleDisplayName === "Super_admin" ? "/media-requests" : (pageUrl || "/");
        setTimeout(() => {
          setLoading(false);
          navigate(dest);
        }, 1500);
      } else if (data.data.retry) {
        // Wrong code — keep the modal open so the user can retry the SAME secret (no re-login,
        // which would rotate the secret and desync their authenticator).
        setCode("");
        toast.error(data.data.errorMessage || "Invalid code. Please try again.");
      } else {
        // Session/token expired — the user must sign in again.
        dispatch(setIsAuthenticated(false));
        dispatch(setUserData([]));
        setStep("");
        setLoading(false);
        toast.error(data.data.errorMessage || "Your session expired. Please sign in again.");
      }
    } catch (error) {
      // Network/unexpected error — keep the modal open so a retry is possible.
      toast.error(MSG.genericError);
      console.error("🚀 ~ Login ~ error:", error);
    } finally {
      setVerifying(false);
    }
  }
  const verifyEnrollment = async (e) => {
    if (verifying) return;                        // guard against double-submit
    setVerifying(true);
    try {
      const postData = {
        tempToken: tempToken,
        token: (code || "").trim()                // strip whitespace (pasted codes)
      }
      const data = await apiCallingPost("api/mfa/verify-totp-enrollment", postData);
      if (data.data.success === "1") {
        const userRow = data.data.result?.userData?.[0];
        if (!userRow || typeof userRow !== "object") {
          dispatch(setIsAuthenticated(false));
          dispatch(setUserData([]));
          setStep("");
          setLoading(false);
          toast.error(data.data.errorMessage || "Login failed. Please contact your administrator.");
          return;
        }
        if (data.data.sessionToken) {
          window.localStorage.setItem("sessionToken", data.data.sessionToken);
        }
        dispatch(setIsAuthenticated(true));
        dispatch(setUserData(userRow));
        setStep("");
        const dest = userRow.roleDisplayName === "Super_admin" ? "/media-requests" : (pageUrl || "/");
        setTimeout(() => {
          setLoading(false);
          navigate(dest);
        }, 1500);
      } else if (data.data.retry) {
        // Wrong code — keep the QR/enroll modal open so the user retries the SAME secret.
        setCode("");
        toast.error(data.data.errorMessage || "Invalid code. Please try again.");
      } else {
        // Session/token expired — the user must sign in again to get a fresh enrollment session.
        dispatch(setIsAuthenticated(false));
        dispatch(setUserData([]));
        setStep("");
        setLoading(false);
        toast.error(data.data.errorMessage || "Your session expired. Please sign in again.");
      }
    } catch (error) {
      // Network/unexpected error — keep the modal open so a retry is possible.
      toast.error(MSG.genericError);
      console.error("🚀 ~ Login ~ error:", error);
    } finally {
      setVerifying(false);
    }
  }

  return (
    <>
      {(!signup && !forgotPassword) ? (
        <div className='right_login'>
          {loading === false ?
            <div className='right_login_inner'>
              <form onSubmit={handleLogin}>
                <h2 className='right_loginhead'>Sign In</h2>
                <div className='py-3 signup-sub-text'>
                  Sign up now and get access to exclusive content!
                </div>
                <div className="form_div">
                  <input
                    type='email'
                    id="username"
                    placeholder="Email"
                    aria-label="Email address"
                    autoComplete="email"
                    value={userName}
                    onChange={(e) => setUserName(e.target.value)}
                    className='form_login'
                  />
                </div>
                <div className="form_div" style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    id="password"
                    placeholder="Password"
                    aria-label="Password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className='form_login'
                  />
                  <span
                    onClick={() => setShowPassword((s) => !s)}
                    role="button"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    title={showPassword ? 'Hide password' : 'Show password'}
                    style={{ position: 'absolute', right: 14, top: 'calc(50% - 10px)', transform: 'translateY(-50%)', cursor: 'pointer', color: '#666', display: 'flex' }}
                  >
                    {showPassword ? <MdVisibilityOff /> : <MdVisibility />}
                  </span>
                </div>
                <p className="forgot-pwd-sec text-end">
                  <span className="signup-text">
                    New User?{" "}
                    <span className="ms-2 cursor-pointer" onClick={() => showSignup(true)}>
                      Sign Up
                    </span>
                  </span>
                </p>
                <button type='submit' disabled={submitFlag} className='login_btn'>
                  Sign In
                </button>

                <p className="forgot-pwd-sec text-end">
                  <span className="signup-text">
                    <span className="ms-2 cursor-pointer" onClick={() => setForgotPassword(true)}>
                      Forgot password
                    </span>
                  </span>
                  <span className="signup-text">
                    <span className="ms-2 cursor-pointer" onClick={handleClick}>
                      Admin Login
                    </span>
                  </span>
                </p>
                <div className='py-3 privacy-text'>
                  By clicking on Sign up, you agree to our Terms of service and Privacy policy.
                </div>
              </form>
              <div className='copyright'>
                © {new Date().getFullYear()} Visit Qatar. All rights reserved.
              </div>
            </div>
            :
            <div className="noData d-flex flex-column justify-content-center align-items-center">
              <svg className="spinner-svg" viewBox="0 0 50 50">
                <circle className="path" cx="25" cy="25" r="20" fill="none" strokeWidth="5"></circle>
              </svg>
            </div>

          }
        </div>

      ) : forgotPassword ? (
        <ForgotPassword forgotPassword={forgotPassword} setForgotPassword={setForgotPassword} />
      ) :

        (
          <SignupForm signup={signup} showSignup={showSignup} />
        )}
      {step === "enroll" && (
        <div className="popup-outer">
          <div className="popup-inner">
            <div className="popup-header">Set up Multi-Factor Authentication</div>
            <div className="popup-body">
              <form onSubmit={(e) => { e.preventDefault(); verifyEnrollment(); }}>
                <p className="mb-1">Secure your account with two-factor authentication:</p>
                <ol className="mb-2 ps-3" style={{ fontSize: '13px', textAlign: 'left' }}>
                  <li>Install an authenticator app (Google Authenticator, Authy, or Microsoft Authenticator).</li>
                  <li>Delete any old <strong>"Qatar Tourism Media Library"</strong> entry from a previous attempt.</li>
                  <li>Scan the QR code below, then enter the current 6-digit code.</li>
                </ol>
                <div className="outer-scanner-img">
                  {qr &&
                    <img src={qr} className="img-fluid scanner-img" alt="MFA QR" width={100} height={100} />}
                </div>

                <div className="mb-3 mt-3">
                  <input type="text" className="form-control" placeholder="Enter the 6-digit code" value={code}
                    onChange={(e) => setCode(e.target.value)} inputMode="numeric" autoFocus />
                  <small className="text-muted d-block mt-1" style={{ fontSize: '12px' }}>
                    Codes not working? Make sure your phone's date &amp; time is set to automatic.
                  </small>
                </div>
                <button type="submit" className="btn btn-primary scanner-btn" disabled={verifying}>{verifying ? "Verifying…" : "Continue to Login"}</button>
              </form>
            </div>
          </div>
        </div>
      )}
      {step === "mfa" && (
        <div className="popup-outer">
          <div className="popup-inner">
            <div className="popup-header">Enter Verification Code</div>
            <div className="popup-body">
              <form onSubmit={(e) => { e.preventDefault(); verifyTotp(); }}>
                <p className="mb-3">Enter the 6-digit code provided by your authenticator app</p>
                <div className="mb-3 mt-3">
                  {/* <label>Verification Code</label> */}
                  <input type="text" className="form-control" placeholder="Enter your Code" value={code}
                    onChange={(e) => setCode(e.target.value)} />
                </div>
                <div className="mb-3">
                  <button type="submit" className="btn btn-primary scanner-btn" disabled={verifying}>{verifying ? "Verifying…" : "Verify"}</button>
                </div>
              </form>
            </div>
          </div>
        </div>)}
    </>
  );
}

export default Login;
